import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { TFacilityMembership } from "api/rpc/2024-04/facilityAdmin/membership/membership";

import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import Sheet from "components/sheet/Sheet";
import { ButtonNew } from "components/buttonNew";

import AttachMembershipModal from "./AttachMembershipModal";

export type TPendingAddonMapping = {
  id?: number;
  modifier_variant_id: number;
  membership_id: number;
  membership?: Pick<TFacilityMembership, "id" | "title" | "type" | "subtitle">;
  modifier_variant?: { id?: number; title?: string };
};

export type TAddonModifierVariant = {
  id: number;
  title: string;
};

export function modifierGroupsToAddonVariants(groups: unknown): TAddonModifierVariant[] {
  const mapped: TAddonModifierVariant[] = [];
  const groupList: unknown[] = Array.isArray(groups) ? (groups as unknown[]) : [];

  for (const group of groupList) {
    if (typeof group !== "object" || group === null) {
      continue;
    }

    const variants = (group as { variants?: unknown }).variants;
    if (!Array.isArray(variants)) {
      continue;
    }

    for (const variant of variants as unknown[]) {
      if (typeof variant !== "object" || variant === null) {
        continue;
      }

      const record = variant as { id?: unknown; title?: unknown; product?: { title?: unknown } };
      const id = Number(record.id);
      if (!Number.isFinite(id)) {
        continue;
      }

      const productTitle = typeof record.product?.title === "string" ? record.product.title : "";
      const title = (typeof record.title === "string" && record.title) || productTitle || `Variant ${id}`;
      mapped.push({ id, title });
    }
  }

  return mapped;
}

type AddonMappingsCardProps = {
  mappings: TPendingAddonMapping[];
  modifierVariants: TAddonModifierVariant[];
  onChange: (mappings: TPendingAddonMapping[]) => void;
  disabled?: boolean;
  userLevel?: "facility" | "client";
};

export default function AddonMappingsCard(props: AddonMappingsCardProps) {
  const { t } = useTranslation();
  const [variantPickerOpen, setVariantPickerOpen] = useState(false);
  const [membershipPickerOpen, setMembershipPickerOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<TAddonModifierVariant | null>(null);

  const mappedVariantIds = new Set(props.mappings.map(row => Number(row.modifier_variant_id)));
  const unmappedVariants = props.modifierVariants.filter(variant => !mappedVariantIds.has(Number(variant.id)));

  function variantTitle(row: TPendingAddonMapping): string {
    return (
      row.modifier_variant?.title ||
      props.modifierVariants.find(variant => Number(variant.id) === Number(row.modifier_variant_id))?.title ||
      `Variant ${row.modifier_variant_id}`
    );
  }

  function handleSelectVariant(variant: TAddonModifierVariant) {
    setSelectedVariant(variant);
    setVariantPickerOpen(false);
    setMembershipPickerOpen(true);
  }

  function handleAttach(membership: TFacilityMembership) {
    if (!selectedVariant) {
      return;
    }

    props.onChange([
      ...props.mappings.filter(row => Number(row.modifier_variant_id) !== Number(selectedVariant.id)),
      {
        modifier_variant_id: selectedVariant.id,
        membership_id: membership.id,
        membership,
        modifier_variant: selectedVariant,
      },
    ]);
    setMembershipPickerOpen(false);
    setSelectedVariant(null);
  }

  function handleRemove(row: TPendingAddonMapping) {
    props.onChange(props.mappings.filter(item => Number(item.modifier_variant_id) !== Number(row.modifier_variant_id)));
  }

  return (
    <>
      <Card
        title={t("Add-on Memberships")}
        subtitle={t("Map modifier variants to the membership each add-on should apply.")}
        titleActions={[
          {
            action: () => setVariantPickerOpen(true),
            content: (
              <>
                <FontAwesomeIcon size="lg" className="mr-2" icon={faPlus} />
                {t("Map add-on")}
              </>
            ),
            buttonType: "text",
            disabled: props.disabled || unmappedVariants.length === 0,
          },
        ]}
      >
        <Card.Section>
          <DataTable columns={[{ label: t("Modifier") }, { label: t("Membership") }, { label: t("Actions") }]}>
            {props.mappings.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  {props.modifierVariants.length === 0
                    ? t("Add modifiers to this product before mapping add-on memberships.")
                    : t("No add-on membership mappings")}
                </td>
              </tr>
            ) : (
              props.mappings.map(row => (
                <tr key={row.modifier_variant_id}>
                  <td>{variantTitle(row)}</td>
                  <td>{row.membership?.title ?? row.membership_id}</td>
                  <td>
                    <ButtonNew
                      type="secondary"
                      size="small"
                      onClick={() => handleRemove(row)}
                      disabled={props.disabled}
                    >
                      {t("Remove")}
                    </ButtonNew>
                  </td>
                </tr>
              ))
            )}
          </DataTable>
        </Card.Section>
      </Card>
      <Sheet
        open={variantPickerOpen}
        size="small"
        closable
        title={t("Select modifier")}
        onCancel={() => setVariantPickerOpen(false)}
        onOk={() => undefined}
        okText={t("Close")}
      >
        <DataTable columns={[{ label: t("Modifier") }]}>
          {unmappedVariants.map(variant => (
            <tr key={variant.id} onClick={() => handleSelectVariant(variant)} style={{ cursor: "pointer" }}>
              <td>{variant.title}</td>
            </tr>
          ))}
        </DataTable>
      </Sheet>
      <AttachMembershipModal
        isVisible={membershipPickerOpen}
        onCancel={() => {
          setMembershipPickerOpen(false);
          setSelectedVariant(null);
        }}
        onOk={membership => handleAttach(membership)}
        allowedMembershipTypes={["membership", "add_on"]}
        userLevel={props.userLevel}
      />
    </>
  );
}
